import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from '@emotion/styled';

type Props = {
  children: string;
  className?: string;
};

/**
 * TalkFlow AI 결과(요약/채팅 답변) 마크다운 렌더러.
 * - 폰트 크기/색은 부모에서 상속받아 AI 요약·AI Chat 어디에 붙어도 톤이 맞는다.
 * - 블록 간격/리스트/제목/코드/표 등 구조만 최소로 스타일링.
 */
const Markdown = ({ children, className }: Props) => (
  <Wrap className={className}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node: _node, ...props }) => (
          <a {...props} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  </Wrap>
);

export default Markdown;

const Wrap = styled.div`
  font: inherit;
  color: inherit;
  overflow-wrap: break-word;
  word-break: break-word;

  /* 블록 요소 간격 — 마지막 블록 아래 여백 제거로 컨테이너와 밀착.
     위 여백은 아래 "* + 제목/인용/구분선" 규칙으로 앞 블록이 있을 때만 주므로 첫 블록은 자동으로 0.
     (:first-child 는 emotion SSR 경고 대상, :first-of-type 은 문서 중간의 첫 h2 까지 걸린다) */
  > *:last-child {
    margin-bottom: 0;
  }

  p {
    margin: 0 0 16px;
  }

  /* 값은 일반적인 마크다운 표기(GitHub 렌더링) 그대로 —
     에디터 테마(public/css/lexical_editor.scss 의 PlaygroundEditorTheme__*)와 같은 값. */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 16px;
    font-weight: 600;
    line-height: 1.25;
  }
  * + h1,
  * + h2,
  * + h3,
  * + h4,
  * + h5,
  * + h6 {
    margin-top: 24px;
  }
  h1 {
    font-size: 2em;
  }
  h2 {
    font-size: 1.5em;
  }
  h3 {
    font-size: 1.25em;
  }
  h4 {
    font-size: 1em;
  }
  h5 {
    font-size: 0.875em;
  }
  /* h6 는 크기로만 구분 — 회색 글자는 GitHub 렌더링의 장식이라 쓰지 않는다. */
  h6 {
    font-size: 0.85em;
  }

  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 2em;
  }
  li + li {
    margin-top: 0.25em;
  }
  li > ul,
  li > ol {
    margin: 0.25em 0 0;
  }
  /* GFM 체크리스트 — 에디터(마커 숨김 + 체크박스)와 같은 모습으로 맞춘다. */
  li.task-list-item {
    list-style: none;
  }
  li.task-list-item input[type='checkbox'] {
    margin-right: 8px;
  }

  strong {
    font-weight: 600;
  }
  em {
    font-style: italic;
  }

  a {
    color: #6a5af9;
    text-decoration: underline;
  }

  blockquote {
    margin: 0 0 16px;
    padding: 0 1em;
    border-left: 0.25em solid var(--dk-line-strong, #d1d9e0);
    color: var(--dk-text-strong, #59636e);
  }

  hr {
    height: 1px;
    margin: 0 0 24px;
    padding: 0;
    border: 0;
    background-color: var(--dk-bg-strong, #d1d9e0);
  }
  * + hr {
    margin-top: 24px;
  }

  code {
    padding: 0.2em 0.4em;
    background: rgba(129, 139, 152, 0.12);
    border-radius: 6px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.85em;
  }
  pre {
    margin: 0 0 16px;
    padding: 16px;
    background: var(--dk-bg-subtle, #f6f8fa);
    border-radius: 6px;
    line-height: 1.45;
    font-size: 0.85em;
    overflow-x: auto;
  }
  /* pre 가 이미 0.85em 이라 안쪽 code 를 또 줄이면 두 번 축소된다. */
  pre code {
    padding: 0;
    background: none;
    font-size: 1em;
  }

  table {
    display: block;
    width: max-content;
    max-width: 100%;
    margin: 0 0 16px;
    border-collapse: collapse;
    overflow-x: auto;
  }
  th,
  td {
    padding: 6px 13px;
    border: 1px solid var(--dk-line-strong, #d1d9e0);
    text-align: left;
  }
  /* 헤더 행은 옅은 배경 + 굵게로 구분한다(Notion 방식). 짝수행 줄무늬는 쓰지 않는다. */
  th {
    background: var(--dk-bg-subtle, #f6f8fa);
    font-weight: 600;
  }
  /* 셀 안 문단의 아래 여백이 셀 높이를 밀어내지 않게. */
  th > :last-child,
  td > :last-child {
    margin-bottom: 0;
  }
`;
